/**
 * Type-safe API client using openapi-fetch and openapi-react-query.
 * 
 * This provides:
 * - Automatic TypeScript types from the OpenAPI schema
 * - React Query integration with caching and deduplication
 * - Consistent error handling
 */

import createClient from "openapi-fetch"
import createQueryClient from "openapi-react-query"
import { clearTokens, getAccessToken, setTokens, getRefreshToken, refreshTokenApi } from "@/lib/auth"
import type { paths } from "./schema"

// Create the base openapi-fetch client
const client = createClient<paths>({
  baseUrl: process.env.NEXT_PUBLIC_API_URL || "",
})

// Ensure all API requests use trailing slashes (Django APPEND_SLASH redirects
// POST/PUT/PATCH/DELETE to GET, breaking mutations without trailing slashes).
//
// Why we read+re-encode the body: `new Request(url, request)` clones the body
// as a ReadableStream. Fetching a Request with a stream body without an
// explicit `duplex: 'half'` is a silent client-side failure in Chrome 105+
// (the request reports responseStatus=0 with transferSize=0 — the network
// panel shows it as "503" but the request never actually left the browser).
// Reading the body as text and re-encoding keeps it as a buffered body so
// the browser sets Content-Length and the request sends cleanly.
client.use({
  async onRequest({ request }) {
    const url = new URL(request.url)
    if (!url.pathname.startsWith("/api/") || url.pathname.endsWith("/")) {
      return request
    }
    url.pathname += "/"
    if (request.method === "GET" || request.method === "HEAD") {
      return new Request(url.toString(), request)
    }
    const body = await request.text()
    return new Request(url.toString(), {
      method: request.method,
      headers: request.headers,
      body: body || undefined,
      credentials: request.credentials,
      mode: request.mode,
      redirect: request.redirect,
      referrer: request.referrer,
      referrerPolicy: request.referrerPolicy,
      integrity: request.integrity,
      cache: request.cache,
    })
  },
})

// Inject JWT auth header and handle 401 with silent refresh
client.use({
  async onRequest({ request }) {
    const token = getAccessToken()
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`)
    }
    return request
  },
  async onResponse({ response, request }) {
    if (response.status !== 401) return response

    // Attempt silent token refresh
    const refresh = getRefreshToken()
    if (!refresh) {
      clearTokens()
      if (typeof window !== "undefined") window.location.href = "/login"
      return response
    }

    try {
      const tokens = await refreshTokenApi(refresh)
      setTokens(tokens.access, tokens.refresh)
      // Retry the original request with the new token
      const retryRequest = new Request(request, { headers: new Headers(request.headers) })
      retryRequest.headers.set("Authorization", `Bearer ${tokens.access}`)
      return fetch(retryRequest)
    } catch {
      clearTokens()
      if (typeof window !== "undefined") window.location.href = "/login"
      return response
    }
  },
})

// Create React Query hooks from the openapi-fetch client
export const $api = createQueryClient(client)

// Re-export the raw client for non-hook usage (e.g., mutations outside components)
export { client }

// Re-export schema types for convenience
export type { paths } from "./schema"
